import { supabase } from '@/integrations/supabase/client';

type QueryClient = {
  from: (table: string) => LooseQueryBuilder;
  rpc: (fn: string, args?: Record<string, unknown>) => Promise<QueryResult>;
};

type QueryResult = {
  data: unknown;
  error: { message: string } | null;
};

type LooseQueryBuilder = PromiseLike<QueryResult> & {
  select: (...args: unknown[]) => LooseQueryBuilder;
  insert: (value: unknown) => LooseQueryBuilder;
  update: (value: unknown) => LooseQueryBuilder;
  order: (...args: unknown[]) => LooseQueryBuilder;
  eq: (...args: unknown[]) => LooseQueryBuilder;
  like: (...args: unknown[]) => LooseQueryBuilder;
  limit: (...args: unknown[]) => LooseQueryBuilder;
  range: (...args: unknown[]) => LooseQueryBuilder;
  single: () => Promise<QueryResult>;
};

const db = supabase as unknown as QueryClient;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const requireData = <T>(label: string, result: { data: T; error: { message: string } | null }) => {
  if (result.error) {
    throw new Error(`${label}: ${result.error.message}`);
  }

  return result.data;
};

const optionalMigrationData = <T>(label: string, result: { data: T; error: { message: string } | null }, fallback: T) => {
  if (!result.error) {
    return result.data;
  }

  const message = result.error.message.toLowerCase();
  if (message.includes('does not exist') || message.includes('could not find')) {
    console.warn(`${label}: optional migration table is not available yet.`);
    return fallback;
  }

  throw new Error(`${label}: ${result.error.message}`);
};

const getFirstRow = <T extends Record<string, unknown>>(result: unknown): T | null => {
  if (!Array.isArray(result) || result.length === 0) {
    return null;
  }

  return result[0] as T;
};

/**
 * Clinic PO numbers continue the paper PO book: the letter F + a running
 * integer, no padding (F816 … F844 on paper). The floor of 845 guarantees the
 * first app-generated number is F845 even before any F-numbers exist in the
 * table. Legacy `PO-YYYYMMDD-xxxxxx` numbers stay in the table untouched —
 * they simply never match the F-pattern and never affect the sequence.
 */
const F_PO_NUMBER_PATTERN = /^F(\d+)$/;
const F_PO_NUMBER_FLOOR = 845;
const PO_NUMBER_MAX_ATTEMPTS = 5;

const nextFPoNumber = async (): Promise<string> => {
  // PostgREST `like` can only prefix-match, so fetch F-prefixed numbers and
  // apply the exact /^F\d+$/ filter client-side (a hypothetical "FOLLOWUP-1"
  // style number can never poison the sequence). Text-sorting can't order the
  // numeric suffix (F999 > F1000 as text), so newest-first by created_at +
  // limit keeps the true max inside the window even past PostgREST's
  // 1000-row response cap — the sequence is monotonic, so the latest rows
  // always contain the highest number.
  const scan = await db
    .from('inventory_purchase_orders')
    .select('po_number')
    .like('po_number', 'F%')
    .order('created_at', { ascending: false })
    .limit(1000);

  const rows = requireData<{ po_number: string | null }[]>(
    'inventory_purchase_orders.po_number_scan',
    scan as unknown as { data: { po_number: string | null }[]; error: { message: string } | null }
  );

  let maxSuffix = 0;
  for (const row of rows ?? []) {
    const match = row.po_number ? F_PO_NUMBER_PATTERN.exec(row.po_number) : null;
    if (match) {
      const suffix = Number.parseInt(match[1], 10);
      if (Number.isFinite(suffix) && suffix > maxSuffix) {
        maxSuffix = suffix;
      }
    }
  }

  return `F${Math.max(maxSuffix + 1, F_PO_NUMBER_FLOOR)}`;
};

/** Postgres unique-violation (23505) as surfaced through PostgREST. */
const isDuplicatePoNumberError = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes('duplicate key') ||
    lower.includes('23505') ||
    (lower.includes('unique') && lower.includes('po_number'))
  );
};

const resolveAppUserId = async (userId: string, userEmail?: string) => {
  if (UUID_PATTERN.test(userId)) {
    const userById = await db
      .from('users')
      .select('id')
      .eq('id', userId)
      .limit(1);
    const user = getFirstRow<{ id: string }>(
      requireData<Record<string, unknown>[]>('users.lookup_by_id', userById)
    );

    if (user?.id) {
      return user.id;
    }
  }

  const normalizedEmail = userEmail?.trim().toLowerCase();

  if (normalizedEmail) {
    const userByEmail = await db
      .from('users')
      .select('id')
      .eq('email', normalizedEmail)
      .limit(1);
    const user = getFirstRow<{ id: string }>(
      requireData<Record<string, unknown>[]>('users.lookup_by_email', userByEmail)
    );

    if (user?.id) {
      return user.id;
    }
  }

  throw new Error('Your saved user session is no longer valid. Please log out and log in again before submitting inventory work.');
};

const toNumber = (value: unknown) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const fetchAllRows = async (
  table: string,
  columns: string,
  options?: {
    orderBy?: string;
    ascending?: boolean;
  }
) => {
  const pageSize = 1000;
  const allRows: Record<string, unknown>[] = [];
  let from = 0;

  while (true) {
    let query = db
      .from(table)
      .select(columns)
      .range(from, from + pageSize - 1);

    if (options?.orderBy) {
      query = query.order(options.orderBy, { ascending: options.ascending ?? true });
    }

    const page = requireData<Record<string, unknown>[]>(
      `${table}.page.${from / pageSize + 1}`,
      await query
    );

    allRows.push(...page);

    if (page.length < pageSize) {
      break;
    }

    from += pageSize;
  }

  return allRows;
};

export interface InventoryItem {
  id: string;
  plato_inventory_id: string;
  given_id: string | null;
  name: string;
  category: string | null;
  description: string | null;
  manufacturer: string | null;
  unit: string | null;
  order_unit: string | null;
  warehouse_on_hand: number;
  track_stock: boolean;
  is_active: boolean;
  is_requestable: boolean;
  cost_price: number | null;
  selling_price: number | null;
  pack_size: number | null;
  inventory_type: string | null;
  remarks: string | null;
  last_plato_edited_at: string | null;
  last_plato_synced_at: string | null;
  raw_plato_payload?: {
    supplier?: string;
    qty?: string | number;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface InventorySupplier {
  id: string;
  plato_supplier_id: string | null;
  name: string;
  email: string | null;
  telephone: string | null;
  handphone: string | null;
  contact_person: string | null;
  address: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryItemSupplier {
  id: string;
  inventory_item_id: string;
  inventory_supplier_id: string;
  is_primary: boolean;
  supplier_sku: string | null;
  supplier_pack_size: number | null;
  minimum_order_qty: number | null;
  reorder_level: number | null;
  lead_time_days: number | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryRoom {
  id: string;
  name: string;
  clinic: string;
  clinic_id: string | null;
  color: string | null;
  status: string;
}

export interface InventoryRoomAccess {
  id: string;
  user_id: string;
  room_id: string;
  can_request: boolean;
}

export interface InventoryRoomRequest {
  id: string;
  room_id: string;
  requested_by: string;
  status: 'draft' | 'submitted' | 'reviewing' | 'partially_fulfilled' | 'fulfilled' | 'cancelled';
  request_notes: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryRoomRequestItem {
  id: string;
  room_request_id: string;
  inventory_item_id: string;
  requested_qty: number;
  approved_qty: number | null;
  fulfilled_qty: number;
  shortage_qty: number;
  item_note: string | null;
  status: 'requested' | 'approved' | 'short' | 'ordered' | 'fulfilled' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export interface InventoryPurchaseOrder {
  id: string;
  supplier_id: string;
  po_number: string;
  status: 'draft' | 'ready' | 'sent' | 'acknowledged' | 'cancelled';
  created_by: string;
  sent_at: string | null;
  sent_to_email: string | null;
  supplier_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryPurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  /**
   * Nullable since migration 20260521130000_po_line_items_free_text.sql —
   * direct-PO lines for items not yet in the catalog have a free-text
   * `item_description` instead of an FK.
   */
  inventory_item_id: string | null;
  item_description: string | null;
  unit_price: number | null;
  uom: string | null;
  qty_to_order: number;
  unit_cost_snapshot: number | null;
  source_request_item_ids: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface InventorySyncLog {
  id: string;
  direction: 'plato_to_app' | 'app_to_plato';
  endpoint: string;
  status: 'success' | 'error' | 'partial';
  summary: string;
  error_message: string | null;
  created_at: string;
}

export interface InventoryIssueBatch {
  id: string;
  batch_number: string;
  status: 'planned' | 'ready_for_picking' | 'partially_issued' | 'issued' | 'plato_failed' | 'cancelled';
  created_by: string;
  confirmed_by: string | null;
  confirmed_at: string | null;
  issue_notes: string | null;
  plato_adjustment_status: 'not_started' | 'pending' | 'succeeded' | 'failed' | 'skipped';
  plato_adjustment_reference: string | null;
  plato_adjustment_error: string | null;
  plato_adjustment_payload: Record<string, unknown> | null;
  plato_adjusted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryIssueBatchItem {
  id: string;
  issue_batch_id: string;
  room_id: string;
  inventory_item_id: string;
  request_item_ids: string[];
  requested_qty: number;
  planned_qty: number;
  issued_qty: number;
  outstanding_qty: number;
  status: 'planned' | 'ready_for_picking' | 'partially_issued' | 'issued' | 'not_issued' | 'cancelled';
  issue_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryMovementLedgerRow {
  movement_id: string;
  movement_type: 'issue' | 'receipt';
  direction: 'in' | 'out';
  occurred_at: string;
  item_id: string | null;
  item_name: string | null;
  item_code: string | null;
  item_unit: string | null;
  qty: number;
  signed_qty: number;
  room_id: string | null;
  room_name: string | null;
  person_user_id: string | null;
  person_name: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  reference: string | null;
  notes: string | null;
  balance_after: number;
}

export interface InventoryWorkflowSnapshot {
  items: InventoryItem[];
  suppliers: InventorySupplier[];
  itemSuppliers: InventoryItemSupplier[];
  rooms: InventoryRoom[];
  roomAccess: InventoryRoomAccess[];
  requests: InventoryRoomRequest[];
  requestItems: InventoryRoomRequestItem[];
  purchaseOrders: InventoryPurchaseOrder[];
  purchaseOrderItems: InventoryPurchaseOrderItem[];
  issueBatches: InventoryIssueBatch[];
  issueBatchItems: InventoryIssueBatchItem[];
  latestSyncLog: InventorySyncLog | null;
}

export interface RequestDraftLineInput {
  inventoryItemId: string;
  requestedQty: number;
  itemNote?: string;
}

export interface StockTakeSession {
  id: string;
  status: 'open' | 'completed';
  note: string | null;
  location: string | null;
  created_by: string | null;
  completed_by: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface StockTakeLine {
  id: string;
  session_id: string;
  inventory_item_id: string;
  counted_qty: number;
  system_qty_snapshot: number;
  variance: number;
  applied: boolean;
  applied_at: string | null;
  created_at: string;
}

export interface CreateStockTakeSessionInput {
  createdBy: string;
  createdByEmail?: string;
  note?: string | null;
  location?: string | null;
}

export interface SaveStockTakeLineInput {
  sessionId: string;
  inventoryItemId: string;
  countedQty: number;
  systemQtySnapshot: number;
}

export interface ApplyStockTakeInput {
  sessionId: string;
  appliedBy: string;
  appliedByEmail?: string;
  /** Required to apply MAJOR variances; small variances apply without it. */
  force?: boolean;
}

/** One row of the read-only stock-take variance preview (preview_stock_take RPC). */
export interface StockTakeVariancePreview {
  line_id: string;
  inventory_item_id: string;
  item_name: string;
  counted_qty: number;
  system_qty: number;
  variance: number;
  variance_pct: number | null; // fraction (0.25 = 25%); null when system_qty = 0
  is_major: boolean;
  applied: boolean;
}

/** One row of the inventory_cycle_count_due worklist view. */
export interface CycleCountDueItem {
  id: string;
  name: string;
  given_id: string | null;
  category: string | null;
  unit: string | null;
  warehouse_on_hand: number;
  abc_class: 'A' | 'B' | 'C';
  last_counted_at: string | null;
  next_count_due: string | null;
  cadence_days: number;
  days_overdue: number;
}

export interface CreatePurchaseOrderInput {
  supplierId: string;
  createdBy: string;
  createdByEmail?: string;
  supplierMessage?: string;
  items: Array<{
    /**
     * Optional — when omitted the line is treated as a free-text item and
     * must carry an `itemDescription`. Catalog-linked lines should pass an
     * inventoryItemId.
     */
    inventoryItemId?: string | null;
    itemDescription?: string | null;
    qtyToOrder: number;
    unitPrice?: number | null;
    uom?: string | null;
    unitCostSnapshot?: number | null;
    sourceRequestItemIds?: string[];
  }>;
}

export interface CreateIssueBatchInput {
  createdBy: string;
  createdByEmail?: string;
  issueNotes?: string;
  items: Array<{
    inventoryItemId: string;
    roomId: string;
    requestedQty: number;
    plannedQty: number;
    requestItemIds: string[];
  }>;
}

export interface ConfirmIssueBatchInput {
  batchId: string;
  confirmedBy: string;
  confirmedByEmail?: string;
  /**
   * Optional client-supplied idempotency key (crypto.randomUUID). When passed,
   * the confirm RPC dedupes a retried submit instead of double-deducting stock.
   */
  idempotencyKey?: string;
  lines: Array<{
    batchItemId: string;
    issuedQty: number;
    issueReason?: string;
  }>;
  /** Manager override to issue beyond on-hand (drives stock negative). */
  allowNegative?: boolean;
  /** Required when allowNegative is true; logged onto the batch line. */
  overrideReason?: string;
}

export interface PlatoAdjustmentResult {
  adjustedCount?: number;
  failedCount?: number;
  platoResponseExcerpt?: unknown;
  error?: string;
}

interface PlatoCountResponse {
  records?: number;
  pages?: number;
}

interface PlatoSyncFunctionEnvelope<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  importedCount?: number;
  linkedSupplierCount?: number;
  pageCount?: number;
  currentPage?: number;
  syncedCount?: number;
  unmatchedCount?: number;
  stockDate?: string;
  stockLocation?: string;
}

export interface InventorySyncProgress {
  resource: 'inventory' | 'supplier';
  phase: 'counting' | 'syncing-suppliers' | 'syncing-inventory' | 'syncing-stock' | 'refreshing';
  mode: 'full' | 'incremental';
  progressPercent: number;
  detail: string;
  platoRecordCount: number;
  platoPageCount: number;
  supplierPage: number;
  inventoryPage: number;
  importedSuppliers: number;
  importedItems: number;
  linkedSupplierCount: number;
  syncedStockItems: number;
  unmatchedStockItems: number;
  stockDate: string | null;
  stockLocation: string | null;
  modifiedSince: string | null;
}

export interface InventorySyncRunResult {
  resource: 'inventory' | 'supplier';
  platoRecordCount: number;
  platoPageCount: number;
  importedSuppliers: number;
  importedItems: number;
  linkedSupplierCount: number;
  syncedStockItems: number;
  unmatchedStockItems: number;
  stockDate: string | null;
  stockLocation: string | null;
  mode: 'full' | 'incremental';
  modifiedSince: string | null;
}

const invokeInventorySync = async <T>(
  body: Record<string, unknown>
): Promise<PlatoSyncFunctionEnvelope<T>> => {
  const { data, error } = await supabase.functions.invoke('plato-inventory-sync', {
    body,
  });

  if (error) {
    throw new Error(`plato-inventory-sync: ${error.message}`);
  }

  const payload = data as PlatoSyncFunctionEnvelope<T> | null;
  if (!payload?.success) {
    throw new Error(payload?.error || 'plato-inventory-sync returned an unsuccessful response');
  }

  return payload;
};

const buildProgressEmitter = (params: {
  resource: 'inventory' | 'supplier';
  mode: 'full' | 'incremental';
  onProgress?: (progress: InventorySyncProgress) => void;
  getState: () => {
    platoRecordCount: number;
    platoPageCount: number;
    supplierPage: number;
    inventoryPage: number;
    importedSuppliers: number;
    importedItems: number;
    linkedSupplierCount: number;
    syncedStockItems: number;
    unmatchedStockItems: number;
    stockDate: string | null;
    stockLocation: string | null;
    modifiedSince: string | null;
  };
}) => {
  return (partial: Partial<InventorySyncProgress>) => {
    if (!params.onProgress) {
      return;
    }

    const state = params.getState();
    params.onProgress({
      resource: params.resource,
      phase: partial.phase ?? 'counting',
      mode: params.mode,
      progressPercent: Math.max(0, Math.min(100, partial.progressPercent ?? 0)),
      detail: partial.detail ?? '',
      platoRecordCount: state.platoRecordCount,
      platoPageCount: state.platoPageCount,
      supplierPage: partial.supplierPage ?? state.supplierPage,
      inventoryPage: partial.inventoryPage ?? state.inventoryPage,
      importedSuppliers: partial.importedSuppliers ?? state.importedSuppliers,
      importedItems: partial.importedItems ?? state.importedItems,
      linkedSupplierCount: partial.linkedSupplierCount ?? state.linkedSupplierCount,
      syncedStockItems: partial.syncedStockItems ?? state.syncedStockItems,
      unmatchedStockItems: partial.unmatchedStockItems ?? state.unmatchedStockItems,
      stockDate: partial.stockDate ?? state.stockDate,
      stockLocation: partial.stockLocation ?? state.stockLocation,
      modifiedSince: state.modifiedSince,
    });
  };
};

export const inventoryService = {
  async getWorkflowSnapshot(clinicId?: string): Promise<InventoryWorkflowSnapshot> {
    const [
      itemRows,
      suppliersResult,
      itemSuppliersResult,
      roomsResult,
      roomAccessResult,
      requestsResult,
      requestItemsResult,
      purchaseOrdersResult,
      purchaseOrderItemsResult,
      issueBatchesResult,
      issueBatchItemsResult,
      syncLogResult,
    ] = await Promise.all([
      fetchAllRows(
        'inventory_items',
        'id, plato_inventory_id, given_id, name, category, description, manufacturer, unit, order_unit, warehouse_on_hand, track_stock, is_active, is_requestable, cost_price, selling_price, pack_size, inventory_type, remarks, last_plato_edited_at, last_plato_synced_at, raw_plato_payload, created_at, updated_at',
        { orderBy: 'name' }
      ),
      db
        .from('inventory_suppliers')
        .select('id, plato_supplier_id, name, email, telephone, handphone, contact_person, address, notes, created_at, updated_at')
        .order('name'),
      db
        .from('inventory_item_suppliers')
        .select('id, inventory_item_id, inventory_supplier_id, is_primary, supplier_sku, supplier_pack_size, minimum_order_qty, reorder_level, lead_time_days, created_at, updated_at'),
      clinicId
        ? db
            .from('rooms')
            .select('id, name, clinic, clinic_id, color, status')
            .eq('clinic_id', clinicId)
            .order('name')
        : db
            .from('rooms')
            .select('id, name, clinic, clinic_id, color, status')
            .order('name'),
      db
        .from('inventory_room_user_access')
        .select('id, user_id, room_id, can_request'),
      db
        .from('inventory_room_requests')
        .select('id, room_id, requested_by, status, request_notes, submitted_at, reviewed_at, reviewed_by, created_at, updated_at')
        .order('created_at', { ascending: false }),
      db
        .from('inventory_room_request_items')
        .select('id, room_request_id, inventory_item_id, requested_qty, approved_qty, fulfilled_qty, shortage_qty, item_note, status, created_at, updated_at')
        .order('created_at', { ascending: false }),
      db
        .from('inventory_purchase_orders')
        .select('id, supplier_id, po_number, status, created_by, sent_at, sent_to_email, supplier_message, created_at, updated_at')
        .order('created_at', { ascending: false }),
      db
        .from('inventory_purchase_order_items')
        .select('id, purchase_order_id, inventory_item_id, item_description, unit_price, uom, qty_to_order, unit_cost_snapshot, source_request_item_ids, created_at, updated_at')
        .order('created_at', { ascending: false }),
      db
        .from('inventory_issue_batches')
        .select('id, batch_number, status, created_by, confirmed_by, confirmed_at, issue_notes, plato_adjustment_status, plato_adjustment_reference, plato_adjustment_error, plato_adjustment_payload, plato_adjusted_at, created_at, updated_at')
        .order('created_at', { ascending: false }),
      db
        .from('inventory_issue_batch_items')
        .select('id, issue_batch_id, room_id, inventory_item_id, request_item_ids, requested_qty, planned_qty, issued_qty, outstanding_qty, status, issue_reason, created_at, updated_at')
        .order('created_at', { ascending: false }),
      db
        .from('inventory_sync_logs')
        .select('id, direction, endpoint, status, summary, error_message, created_at')
        .order('created_at', { ascending: false })
        .limit(1),
    ]);

    const items = itemRows.map((row) => ({
      ...row,
      warehouse_on_hand: toNumber(row.warehouse_on_hand),
      cost_price: row.cost_price == null ? null : toNumber(row.cost_price),
      selling_price: row.selling_price == null ? null : toNumber(row.selling_price),
      pack_size: row.pack_size == null ? null : toNumber(row.pack_size),
    })) as InventoryItem[];

    const itemSuppliers = requireData<Record<string, unknown>[]>('inventory_item_suppliers', itemSuppliersResult).map((row) => ({
      ...row,
      supplier_pack_size: row.supplier_pack_size == null ? null : toNumber(row.supplier_pack_size),
      minimum_order_qty: row.minimum_order_qty == null ? null : toNumber(row.minimum_order_qty),
      reorder_level: row.reorder_level == null ? null : toNumber(row.reorder_level),
    })) as InventoryItemSupplier[];

    const requestItems = requireData<Record<string, unknown>[]>('inventory_room_request_items', requestItemsResult).map((row) => ({
      ...row,
      requested_qty: toNumber(row.requested_qty),
      approved_qty: row.approved_qty == null ? null : toNumber(row.approved_qty),
      fulfilled_qty: toNumber(row.fulfilled_qty),
      shortage_qty: toNumber(row.shortage_qty),
    })) as InventoryRoomRequestItem[];

    const purchaseOrderItems = requireData<Record<string, unknown>[]>('inventory_purchase_order_items', purchaseOrderItemsResult).map((row) => ({
      ...row,
      qty_to_order: toNumber(row.qty_to_order),
      unit_cost_snapshot: row.unit_cost_snapshot == null ? null : toNumber(row.unit_cost_snapshot),
      unit_price: row.unit_price == null ? null : toNumber(row.unit_price),
      item_description: typeof row.item_description === 'string' ? row.item_description : null,
      uom: typeof row.uom === 'string' ? row.uom : null,
      source_request_item_ids: Array.isArray(row.source_request_item_ids) ? row.source_request_item_ids : null,
    })) as InventoryPurchaseOrderItem[];

    const issueBatches = optionalMigrationData<Record<string, unknown>[]>(
      'inventory_issue_batches',
      issueBatchesResult,
      []
    ) as InventoryIssueBatch[];

    const issueBatchItems = optionalMigrationData<Record<string, unknown>[]>(
      'inventory_issue_batch_items',
      issueBatchItemsResult,
      []
    ).map((row) => ({
      ...row,
      request_item_ids: Array.isArray(row.request_item_ids) ? row.request_item_ids : [],
      requested_qty: toNumber(row.requested_qty),
      planned_qty: toNumber(row.planned_qty),
      issued_qty: toNumber(row.issued_qty),
      outstanding_qty: toNumber(row.outstanding_qty),
    })) as InventoryIssueBatchItem[];

    const latestSyncLog = (
      requireData<Record<string, unknown>[]>('inventory_sync_logs', syncLogResult)[0] ?? null
    ) as InventorySyncLog | null;

    return {
      items,
      suppliers: requireData<InventorySupplier[]>('inventory_suppliers', suppliersResult),
      itemSuppliers,
      rooms: requireData<InventoryRoom[]>('rooms', roomsResult),
      roomAccess: requireData<InventoryRoomAccess[]>('inventory_room_user_access', roomAccessResult),
      requests: requireData<InventoryRoomRequest[]>('inventory_room_requests', requestsResult),
      requestItems,
      purchaseOrders: requireData<InventoryPurchaseOrder[]>('inventory_purchase_orders', purchaseOrdersResult),
      purchaseOrderItems,
      issueBatches,
      issueBatchItems,
      latestSyncLog,
    };
  },

  /**
   * C: read-only dispense / stock-movement ledger. Backed by the
   * inventory_movement_ledger view (issues out + receipts in, with room/person/
   * actor names and a movement-based running balance). Returns newest-first;
   * the view is optional during phased rollout (returns [] if not yet applied).
   */
  async getMovementLedger(): Promise<InventoryMovementLedgerRow[]> {
    const pageSize = 1000;
    const rows: Record<string, unknown>[] = [];
    let from = 0;

    while (true) {
      const result = await db
        .from('inventory_movement_ledger')
        .select(
          'movement_id, movement_type, direction, occurred_at, item_id, item_name, item_code, item_unit, qty, signed_qty, room_id, room_name, person_user_id, person_name, actor_user_id, actor_name, reference, notes, balance_after'
        )
        .order('occurred_at', { ascending: false })
        .order('movement_id', { ascending: false })
        .range(from, from + pageSize - 1);

      const page = optionalMigrationData<Record<string, unknown>[]>(
        `inventory_movement_ledger.page.${from / pageSize + 1}`,
        await result,
        []
      );

      rows.push(...page);

      if (page.length < pageSize) {
        break;
      }

      from += pageSize;
    }

    return rows.map((row) => ({
      ...row,
      qty: toNumber(row.qty),
      signed_qty: toNumber(row.signed_qty),
      balance_after: toNumber(row.balance_after),
    })) as InventoryMovementLedgerRow[];
  },

  async submitRoomRequest(params: {
    roomId: string;
    requestedBy: string;
    requestedByEmail?: string;
    requestNotes?: string;
    items: RequestDraftLineInput[];
  }) {
    const submittedAt = new Date().toISOString();
    const requestedBy = await resolveAppUserId(params.requestedBy, params.requestedByEmail);

    const requestInsert = await db
      .from('inventory_room_requests')
      .insert({
        room_id: params.roomId,
        requested_by: requestedBy,
        status: 'submitted',
        request_notes: params.requestNotes?.trim() || null,
        submitted_at: submittedAt,
      })
      .select('id')
      .single();

    const request = requireData<{ id: string }>('inventory_room_requests.insert', requestInsert);

    const itemInsert = await db
      .from('inventory_room_request_items')
      .insert(
        params.items.map((item) => ({
          room_request_id: request.id,
          inventory_item_id: item.inventoryItemId,
          requested_qty: item.requestedQty,
          item_note: item.itemNote?.trim() || null,
          status: 'requested',
        }))
      )
      .select('id');

    requireData('inventory_room_request_items.insert', itemInsert);
    return request.id;
  },

  async createPurchaseOrderDraft(params: CreatePurchaseOrderInput) {
    const createdBy = await resolveAppUserId(params.createdBy, params.createdByEmail);

    // Allocate the next F-number, insert, and on a unique-constraint race
    // (two sessions grabbing the same number at once) re-query and retry.
    // Sequential callers (e.g. the warehouse shortage flow creating one PO
    // per supplier in a loop) naturally get consecutive numbers because each
    // call re-queries the table after the previous insert has committed.
    let purchaseOrder: { id: string; po_number: string } | null = null;
    let lastErrorMessage = '';

    for (let attempt = 0; attempt < PO_NUMBER_MAX_ATTEMPTS; attempt += 1) {
      const poNumber = await nextFPoNumber();

      const poInsert = await db
        .from('inventory_purchase_orders')
        .insert({
          supplier_id: params.supplierId,
          po_number: poNumber,
          created_by: createdBy,
          supplier_message: params.supplierMessage?.trim() || null,
          status: 'draft',
        })
        .select('id, po_number')
        .single();

      if (!poInsert.error) {
        purchaseOrder = poInsert.data as { id: string; po_number: string };
        break;
      }

      lastErrorMessage = poInsert.error.message;
      if (!isDuplicatePoNumberError(lastErrorMessage)) {
        throw new Error(`inventory_purchase_orders.insert: ${lastErrorMessage}`);
      }
      // Duplicate po_number — another session won the race. Loop re-queries
      // for a fresh max and tries the next number.
    }

    if (!purchaseOrder) {
      throw new Error(
        `inventory_purchase_orders.insert: could not allocate a unique PO number after ${PO_NUMBER_MAX_ATTEMPTS} attempts (${lastErrorMessage})`
      );
    }

    const itemInsert = await db
      .from('inventory_purchase_order_items')
      .insert(
        params.items.map((item) => {
          const trimmedDescription = item.itemDescription?.trim();
          const description = trimmedDescription ? trimmedDescription : null;
          const inventoryItemId = item.inventoryItemId ?? null;

          if (!inventoryItemId && !description) {
            throw new Error(
              'inventory_purchase_order_items.insert: each line must reference a catalog item or include a free-text description.'
            );
          }

          return {
            purchase_order_id: purchaseOrder.id,
            inventory_item_id: inventoryItemId,
            item_description: description,
            qty_to_order: item.qtyToOrder,
            unit_price: item.unitPrice ?? null,
            uom: item.uom?.trim() ? item.uom.trim() : null,
            unit_cost_snapshot: item.unitCostSnapshot ?? null,
            source_request_item_ids: item.sourceRequestItemIds ?? null,
          };
        })
      )
      .select('id');

    requireData('inventory_purchase_order_items.insert', itemInsert);
    return purchaseOrder;
  },

  async createIssueBatchFromPlan(params: CreateIssueBatchInput) {
    const stamp = new Date();
    const createdBy = await resolveAppUserId(params.createdBy, params.createdByEmail);
    const batchNumber = `ISS-${stamp.getFullYear()}${String(stamp.getMonth() + 1).padStart(2, '0')}${String(
      stamp.getDate()
    ).padStart(2, '0')}-${stamp.getTime().toString().slice(-6)}`;
    const lines = params.items.filter((item) => item.plannedQty > 0);

    if (lines.length === 0) {
      throw new Error('At least one planned issue line is required.');
    }

    const batchInsert = await db
      .from('inventory_issue_batches')
      .insert({
        batch_number: batchNumber,
        created_by: createdBy,
        issue_notes: params.issueNotes?.trim() || null,
        status: 'ready_for_picking',
      })
      .select('id, batch_number')
      .single();

    const issueBatch = requireData<{ id: string; batch_number: string }>(
      'inventory_issue_batches.insert',
      batchInsert
    );

    const lineInsert = await db
      .from('inventory_issue_batch_items')
      .insert(
        lines.map((line) => ({
          issue_batch_id: issueBatch.id,
          inventory_item_id: line.inventoryItemId,
          room_id: line.roomId,
          request_item_ids: line.requestItemIds,
          requested_qty: line.requestedQty,
          planned_qty: line.plannedQty,
          outstanding_qty: Math.max(line.requestedQty - line.plannedQty, 0),
          status: 'ready_for_picking',
        }))
      )
      .select('id');

    requireData('inventory_issue_batch_items.insert', lineInsert);
    return issueBatch;
  },

  async confirmIssueBatch(params: ConfirmIssueBatchInput) {
    const confirmedBy = await resolveAppUserId(params.confirmedBy, params.confirmedByEmail);
    const finalLines = params.lines.map((line) => ({
      batch_item_id: line.batchItemId,
      issued_qty: line.issuedQty,
      issue_reason: line.issueReason?.trim() || null,
    }));

    const confirmation = await db.rpc('confirm_inventory_issue_batch', {
      batch_id: params.batchId,
      confirmed_by_user_id: confirmedBy,
      final_lines: finalLines,
      ...(params.idempotencyKey ? { p_idempotency_key: params.idempotencyKey } : {}),
      p_allow_negative: params.allowNegative ?? false,
      p_override_reason: params.overrideReason?.trim() || null,
    });

    const confirmed = requireData<Record<string, unknown>[]>(
      'confirm_inventory_issue_batch',
      confirmation
    );
    const platoAdjustment = await this.retryPlatoAdjustment(params.batchId);

    return {
      confirmation: confirmed[0] ?? null,
      platoAdjustment,
    };
  },

  async retryPlatoAdjustment(batchId: string): Promise<PlatoAdjustmentResult> {
    const payload = await invokeInventorySync<PlatoAdjustmentResult>({
      action: 'create_stock_adjustment',
      issueBatchId: batchId,
    });

    return (payload.data ?? payload) as PlatoAdjustmentResult;
  },

  // ---------------------------------------------------------------------
  // Stock-take / cycle-count
  // ---------------------------------------------------------------------

  async listStockTakeSessions(): Promise<StockTakeSession[]> {
    const result = await db
      .from('stock_take_sessions')
      .select('id, status, note, location, created_by, completed_by, completed_at, created_at')
      .order('created_at', { ascending: false })
      .limit(50);

    const rows = optionalMigrationData<Record<string, unknown>[]>(
      'stock_take_sessions.list',
      await result,
      []
    );

    return rows as unknown as StockTakeSession[];
  },

  async listStockTakeLines(sessionId: string): Promise<StockTakeLine[]> {
    const result = await db
      .from('stock_take_lines')
      .select('id, session_id, inventory_item_id, counted_qty, system_qty_snapshot, variance, applied, applied_at, created_at')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    const rows = optionalMigrationData<Record<string, unknown>[]>(
      'stock_take_lines.list',
      await result,
      []
    );

    return rows.map((row) => ({
      ...row,
      counted_qty: toNumber(row.counted_qty),
      system_qty_snapshot: toNumber(row.system_qty_snapshot),
      variance: toNumber(row.variance),
    })) as StockTakeLine[];
  },

  async createStockTakeSession(params: CreateStockTakeSessionInput): Promise<StockTakeSession> {
    const createdBy = await resolveAppUserId(params.createdBy, params.createdByEmail);

    const insert = await db
      .from('stock_take_sessions')
      .insert({
        created_by: createdBy,
        note: params.note?.trim() || null,
        location: params.location?.trim() || null,
        status: 'open',
      })
      .select('id, status, note, location, created_by, completed_by, completed_at, created_at')
      .single();

    return requireData<StockTakeSession>('stock_take_sessions.insert', insert);
  },

  /**
   * Upserts a single count line. There is a UNIQUE(session_id, inventory_item_id)
   * constraint, so we update-if-exists, otherwise insert. The system snapshot is
   * only set on first insert (it captures on-hand at count time and must not move
   * when the counted qty is edited).
   */
  async saveStockTakeLine(params: SaveStockTakeLineInput): Promise<StockTakeLine> {
    const existingResult = await db
      .from('stock_take_lines')
      .select('id')
      .eq('session_id', params.sessionId)
      .eq('inventory_item_id', params.inventoryItemId)
      .limit(1);

    const existing = getFirstRow<{ id: string }>(
      requireData<Record<string, unknown>[]>('stock_take_lines.lookup', existingResult)
    );

    if (existing?.id) {
      const update = await db
        .from('stock_take_lines')
        .update({ counted_qty: params.countedQty })
        .eq('id', existing.id)
        .select('id, session_id, inventory_item_id, counted_qty, system_qty_snapshot, variance, applied, applied_at, created_at')
        .single();

      return requireData<StockTakeLine>('stock_take_lines.update', update);
    }

    const insert = await db
      .from('stock_take_lines')
      .insert({
        session_id: params.sessionId,
        inventory_item_id: params.inventoryItemId,
        counted_qty: params.countedQty,
        system_qty_snapshot: params.systemQtySnapshot,
      })
      .select('id, session_id, inventory_item_id, counted_qty, system_qty_snapshot, variance, applied, applied_at, created_at')
      .single();

    return requireData<StockTakeLine>('stock_take_lines.insert', insert);
  },

  /**
   * Commits all unapplied variances to warehouse_on_hand and closes the session.
   * The RPC is idempotent (it skips lines already marked applied).
   */
  async applyStockTake(params: ApplyStockTakeInput) {
    const appliedBy = await resolveAppUserId(params.appliedBy, params.appliedByEmail);

    const result = await db.rpc('apply_stock_take', {
      p_session_id: params.sessionId,
      p_applied_by: appliedBy,
      p_force: params.force ?? false,
    });

    const rows = requireData<Record<string, unknown>[]>('apply_stock_take', result);
    return (rows?.[0] ?? null) as
      | {
          session_id: string;
          lines_applied: number;
          major_applied: number;
          new_status: string;
        }
      | null;
  },

  /**
   * Read-only variance preview for a stock-take session. Returns one row per
   * counted line with the variance, variance %, and a `is_major` flag so the UI
   * can warn before applying. Never mutates stock.
   */
  async previewStockTake(sessionId: string): Promise<StockTakeVariancePreview[]> {
    const result = await db.rpc('preview_stock_take', { p_session_id: sessionId });
    const rows = requireData<Record<string, unknown>[]>('preview_stock_take', result);
    return (rows ?? []) as unknown as StockTakeVariancePreview[];
  },

  /**
   * ABC cycle-count worklist: items due (or overdue) for a count today.
   * Reads the inventory_cycle_count_due view.
   */
  async listCycleCountDue(limit = 8): Promise<CycleCountDueItem[]> {
    const result = await db
      .from('inventory_cycle_count_due')
      .select('*')
      .limit(limit);
    const rows = requireData<Record<string, unknown>[]>(
      'inventory_cycle_count_due.list',
      result
    );
    return (rows ?? []) as unknown as CycleCountDueItem[];
  },

  async updatePurchaseOrderStatus(id: string, status: InventoryPurchaseOrder['status']) {
    const updatePayload: Record<string, unknown> = { status };

    if (status === 'sent') {
      updatePayload.sent_at = new Date().toISOString();
    }

    const result = await db
      .from('inventory_purchase_orders')
      .update(updatePayload)
      .eq('id', id)
      .select('id')
      .single();

    return requireData<{ id: string }>('inventory_purchase_orders.update', result);
  },

  /**
   * Persists the order-unit / pack-size pairing used for PO ↔ stock unit
   * conversion (e.g. order in boxes, dispense in pieces). `packSize` is the
   * number of dispense units per order unit.
   */
  async updateInventoryItemOrderConfig(
    id: string,
    params: { orderUnit: string | null; packSize: number | null }
  ) {
    const result = await db
      .from('inventory_items')
      .update({
        order_unit: params.orderUnit?.trim() || null,
        pack_size: params.packSize != null && params.packSize > 0 ? params.packSize : null,
      })
      .eq('id', id)
      .select('id, order_unit, pack_size')
      .single();

    return requireData<{ id: string; order_unit: string | null; pack_size: number | null }>(
      'inventory_items.update_order_config',
      result
    );
  },

  async updateInventoryItemActiveState(id: string, isActive: boolean) {
    const result = await db
      .from('inventory_items')
      .update({ is_active: isActive })
      .eq('id', id)
      .select('id, is_active')
      .single();

    return requireData<{ id: string; is_active: boolean }>('inventory_items.update_active_state', result);
  },

  async syncPlatoInventory(
    onProgress?: (progress: InventorySyncProgress) => void
  ): Promise<InventorySyncRunResult> {
    const latestSyncResult = await db
      .from('inventory_items')
      .select('last_plato_synced_at')
      .order('last_plato_synced_at', { ascending: false })
      .limit(1);

    const latestSyncRows = requireData<Record<string, unknown>[]>(
      'inventory_items.latest_sync',
      await latestSyncResult
    );

    const latestSyncAt = latestSyncRows[0]?.last_plato_synced_at;
    const modifiedSince = latestSyncAt
      ? String(Math.floor(new Date(String(latestSyncAt)).getTime() / 1000))
      : null;
    const mode = (modifiedSince ? 'incremental' : 'full') as const;

    const countPayload = await invokeInventorySync<PlatoCountResponse>({
      action: 'count',
    });

    const platoRecordCount = Number(countPayload.data?.records ?? 0);
    const platoPageCount = Number(countPayload.data?.pages ?? 0);
    let importedSuppliers = 0;
    let importedItems = 0;
    let linkedSupplierCount = 0;
    let syncedStockItems = 0;
    let unmatchedStockItems = 0;
    let stockDate: string | null = null;
    let stockLocation: string | null = null;
    let supplierPage = 0;
    let inventoryPage = 0;
    const emitProgress = buildProgressEmitter({
      resource: 'inventory',
      mode,
      onProgress,
      getState: () => ({
        platoRecordCount,
        platoPageCount,
        supplierPage,
        inventoryPage,
        importedSuppliers,
        importedItems,
        linkedSupplierCount,
        syncedStockItems,
        unmatchedStockItems,
        stockDate,
        stockLocation,
        modifiedSince,
      }),
    });

    emitProgress({
      phase: 'counting',
      progressPercent: 8,
      detail: `Plato reports ${platoRecordCount} inventory records across ${platoPageCount || 0} page(s).`,
    });

    supplierPage = 1;

    while (true) {
      emitProgress({
        phase: 'syncing-suppliers',
        supplierPage,
        progressPercent: Math.min(18, 8 + supplierPage * 2),
        detail: `Syncing supplier page ${supplierPage}.`,
      });

      const supplierPayload = await invokeInventorySync({
        action: 'import_suppliers_page',
        currentPage: supplierPage,
      });

      if (!supplierPayload.pageCount) {
        break;
      }

      importedSuppliers += Number(supplierPayload.importedCount ?? 0);
      emitProgress({
        phase: 'syncing-suppliers',
        supplierPage,
        importedSuppliers,
        progressPercent: Math.min(20, 10 + supplierPage * 2),
        detail: `Imported ${importedSuppliers} supplier record(s) through page ${supplierPage}.`,
      });
      supplierPage += 1;
    }

    inventoryPage = 1;

    while (true) {
      const inventoryProgress = platoPageCount > 0
        ? 20 + Math.min(75, (inventoryPage / platoPageCount) * 75)
        : 20;

      emitProgress({
        phase: 'syncing-inventory',
        inventoryPage,
        progressPercent: inventoryProgress,
        detail: platoPageCount > 0
          ? `Syncing inventory page ${inventoryPage} of ${platoPageCount}.`
          : `Syncing inventory page ${inventoryPage}.`,
      });

      const inventoryPayload = await invokeInventorySync({
        action: 'import_inventory_page',
        currentPage: inventoryPage,
        modifiedSince: modifiedSince ?? undefined,
      });

      if (!inventoryPayload.pageCount) {
        break;
      }

      importedItems += Number(inventoryPayload.importedCount ?? 0);
      linkedSupplierCount += Number(inventoryPayload.linkedSupplierCount ?? 0);
      emitProgress({
        phase: 'syncing-inventory',
        inventoryPage,
        importedItems,
        linkedSupplierCount,
        progressPercent: inventoryProgress,
        detail: platoPageCount > 0
          ? `Imported ${importedItems} inventory record(s) through page ${inventoryPage} of ${platoPageCount}.`
          : `Imported ${importedItems} inventory record(s) through page ${inventoryPage}.`,
      });
      inventoryPage += 1;
    }

    emitProgress({
      phase: 'syncing-stock',
      progressPercent: 96,
      detail: 'Refreshing warehouse stock from the Plato stock snapshot.',
    });

    const stockPayload = await invokeInventorySync({
      action: 'sync_inventory_stock',
    });

    syncedStockItems = Number(stockPayload.syncedCount ?? 0);
    unmatchedStockItems = Number(stockPayload.unmatchedCount ?? 0);
    stockDate = stockPayload.stockDate ?? null;
    stockLocation = stockPayload.stockLocation ?? null;

    emitProgress({
      phase: 'syncing-stock',
      progressPercent: 99,
      syncedStockItems,
      unmatchedStockItems,
      stockDate,
      stockLocation,
      detail: stockDate && stockLocation
        ? `Updated ${syncedStockItems} stock value(s) from Plato ${stockLocation} snapshot for ${stockDate}.`
        : `Updated ${syncedStockItems} stock value(s) from the Plato stock snapshot.`,
    });

    emitProgress({
      phase: 'refreshing',
      progressPercent: 100,
      importedSuppliers,
      importedItems,
      linkedSupplierCount,
      syncedStockItems,
      unmatchedStockItems,
      stockDate,
      stockLocation,
      detail: 'Sync finished. Refreshing the catalog snapshot.',
    });

    return {
      resource: 'inventory',
      platoRecordCount,
      platoPageCount,
      importedSuppliers,
      importedItems,
      linkedSupplierCount,
      syncedStockItems,
      unmatchedStockItems,
      stockDate,
      stockLocation,
      mode,
      modifiedSince,
    };
  },

  async syncPlatoSuppliers(
    onProgress?: (progress: InventorySyncProgress) => void
  ): Promise<InventorySyncRunResult> {
    const supplierCountPayload = await invokeInventorySync<PlatoCountResponse>({
      action: 'supplier_count',
    });

    const platoRecordCount = Number(supplierCountPayload.data?.records ?? 0);
    const platoPageCount = Number(supplierCountPayload.data?.pages ?? 0);
    let importedSuppliers = 0;
    const importedItems = 0;
    const linkedSupplierCount = 0;
    const syncedStockItems = 0;
    const unmatchedStockItems = 0;
    const stockDate = null;
    const stockLocation = null;
    let supplierPage = 0;
    const inventoryPage = 0;
    const modifiedSince = null;
    const mode = 'full' as const;
    const emitProgress = buildProgressEmitter({
      resource: 'supplier',
      mode,
      onProgress,
      getState: () => ({
        platoRecordCount,
        platoPageCount,
        supplierPage,
        inventoryPage,
        importedSuppliers,
        importedItems,
        linkedSupplierCount,
        syncedStockItems,
        unmatchedStockItems,
        stockDate,
        stockLocation,
        modifiedSince,
      }),
    });

    emitProgress({
      phase: 'counting',
      progressPercent: 10,
      detail: `Plato reports ${platoRecordCount} supplier records across ${platoPageCount || 0} page(s).`,
    });

    supplierPage = 1;

    while (true) {
      const supplierProgress = platoPageCount > 0
        ? 10 + Math.min(85, (supplierPage / platoPageCount) * 85)
        : 10;

      emitProgress({
        phase: 'syncing-suppliers',
        supplierPage,
        progressPercent: supplierProgress,
        detail: platoPageCount > 0
          ? `Syncing supplier page ${supplierPage} of ${platoPageCount}.`
          : `Syncing supplier page ${supplierPage}.`,
      });

      const supplierPayload = await invokeInventorySync({
        action: 'import_suppliers_page',
        currentPage: supplierPage,
      });

      if (!supplierPayload.pageCount) {
        break;
      }

      importedSuppliers += Number(supplierPayload.importedCount ?? 0);
      emitProgress({
        phase: 'syncing-suppliers',
        supplierPage,
        importedSuppliers,
        progressPercent: supplierProgress,
        detail: platoPageCount > 0
          ? `Imported ${importedSuppliers} supplier record(s) through page ${supplierPage} of ${platoPageCount}.`
          : `Imported ${importedSuppliers} supplier record(s) through page ${supplierPage}.`,
      });
      supplierPage += 1;
    }

    emitProgress({
      phase: 'refreshing',
      progressPercent: 100,
      importedSuppliers,
      detail: 'Supplier sync finished. Refreshing the catalog snapshot.',
    });

    return {
      resource: 'supplier',
      platoRecordCount,
      platoPageCount,
      importedSuppliers,
      importedItems,
      linkedSupplierCount,
      syncedStockItems,
      unmatchedStockItems,
      stockDate,
      stockLocation,
      mode,
      modifiedSince,
    };
  },
};
